// biome-ignore-all lint/a11y/useSemanticElements: <div role="grid">
//   + <div role="row"> is the canonical ARIA-grid pattern for a 6×6
//   game board (NOT tabular data). <table>/<tr> would imply data-row
//   semantics inappropriate for a chess/word board. UI-SPEC §5.1
//   row 10 mandates the same pattern. role="grid" +
//   role="row" + role="gridcell" satisfies WAI-ARIA APG Grid pattern
//   (https://www.w3.org/WAI/ARIA/apg/patterns/grid/).
// biome-ignore-all lint/suspicious/noArrayIndexKey: the 6 rows and
//   36 cells in a 6×6 board are stable, ordered, and never reordered
//   — index IS the identity here.
// biome-ignore-all lint/a11y/useFocusableInteractive: role="row" is
//   a structural ARIA role; per WAI-ARIA APG Grid pattern, focus
//   moves between role="gridcell" buttons (which ARE focusable) —
//   the row wrapper is non-interactive structure and should not be
//   in the focus order. Adding tabIndex to row would actively harm
//   keyboard navigation.
//
// Phase 17 Plan 02 Task 2 — Board primitive.
//
// 6×6 board surface consuming the `.board` + `.cell` recipes in
// apps/web/src/styles/pieces.css. Composes <Cube> in letters view
// and <Piece> in chess view. Each cell is a focusable <button> with
// ARIA grid semantics (role="gridcell" + aria-rowindex/colindex)
// inside a parent role="grid" with aria-rowcount=6 / aria-colcount=6.
//
// Recipe source: apps/web/src/styles/pieces.css `.board` (take-two re-skin).
//
// RESEARCH note (CONTEXT.md §code_context): MatchGrid.tsx (Phase 14
// product) is the parallel-DOM a11y contract (MUI-07). This new Board
// is the visual primitive that the W3 Match rebuild composes — both
// coexist for now; W3 owns the final wiring choice.
//
// Default size = 343px per UI-SPEC §6.2 mobile-primary; passed
// through as `--board-size` CSS var (the .board recipe reads it).

import type { CSSProperties, JSX } from 'react'

import { Cube } from './Cube'
import { Piece, type PieceKind, type PlayerSide } from './Piece'

export interface PieceData {
  kind: PieceKind
  player: PlayerSide
  empowered?: boolean
}

export interface BoardProps {
  view: 'chess' | 'letters'
  /** Length 36 when view='letters'. Indexed row-major from top-left. */
  letters?: ReadonlyArray<string | null>
  /** Length 36 when view='chess'. Indexed row-major from top-left. */
  pieces?: ReadonlyArray<PieceData | null>
  /** Letters word-trace path; cells become .traced. */
  traceIndices?: ReadonlyArray<number>
  /** Last move highlight (single cell). */
  lastMove?: number | null
  selectedIdx?: number | null
  validIdxs?: ReadonlyArray<number>
  empowerTargets?: ReadonlyArray<number>
  onCellTap?: (idx: number) => void
  droppingIdxs?: ReadonlyArray<number>
  pulsingIdxs?: ReadonlyArray<number>
  frozenIdxs?: ReadonlyArray<number>
  /** Cells tinted player-A private. */
  privateAIdxs?: ReadonlyArray<number>
  /** Cells tinted player-B private. */
  privateBIdxs?: ReadonlyArray<number>
  /** Px; default 343 (UI-SPEC §6.2). Threaded as --board-size. */
  size?: number
}

const COLS = 6
const ROWS = 6
const DEFAULT_SIZE = 343

function cellClassName(idx: number, props: BoardProps): string {
  const {
    lastMove,
    selectedIdx,
    validIdxs = [],
    empowerTargets = [],
    droppingIdxs = [],
    pulsingIdxs = [],
    frozenIdxs = [],
    privateAIdxs = [],
    privateBIdxs = [],
  } = props
  return [
    'cell',
    idx === lastMove ? 'last-move' : null,
    idx === selectedIdx ? 'selected' : null,
    validIdxs.includes(idx) ? 'valid' : null,
    empowerTargets.includes(idx) ? 'empower-target' : null,
    droppingIdxs.includes(idx) ? 'drop' : null,
    pulsingIdxs.includes(idx) ? 'pulse' : null,
    frozenIdxs.includes(idx) ? 'frozen' : null,
    privateAIdxs.includes(idx) ? 'private-a' : null,
    privateBIdxs.includes(idx) ? 'private-b' : null,
  ]
    .filter(Boolean)
    .join(' ')
}

export function Board(props: BoardProps): JSX.Element {
  const {
    view,
    letters = [],
    pieces = [],
    traceIndices = [],
    onCellTap,
    droppingIdxs = [],
    frozenIdxs = [],
    pulsingIdxs = [],
    selectedIdx,
    size = DEFAULT_SIZE,
  } = props

  // --board-size is a component-scoped sizing var (not a token; see
  // Pitfall 3); the .board recipe consumes it via `var(--board-size)`.
  const boardStyle = { '--board-size': `${size}px` } as CSSProperties

  return (
    <div
      className="board"
      role="grid"
      aria-rowcount={ROWS}
      aria-colcount={COLS}
      style={boardStyle}
    >
      {Array.from({ length: ROWS }).map((_, rowIdx) => {
        const rowIndex = rowIdx + 1
        return (
          // ARIA grid pattern requires role="row" between role="grid"
          // and role="gridcell" (aria-required-parent). The visual
          // layout still uses the .board recipe's 6×6 CSS Grid;
          // role="row" parents are display:contents so they don't
          // disturb the grid track layout.
          <div
            key={rowIdx}
            role="row"
            aria-rowindex={rowIndex}
            style={{ display: 'contents' }}
          >
            {Array.from({ length: COLS }).map((__, colIdx) => {
              const idx = rowIdx * COLS + colIdx
              const colIndex = colIdx + 1
              const className = cellClassName(idx, props)
              const piece = pieces[idx]
              const letter = letters[idx]
              // Cell aria-label keeps empty cells reachable + named
              // (axe "button must have discernible text"). Cube/Piece
              // children, when present, also supply their own
              // role="img" label inside the button.
              const cellLabel = `row ${rowIndex} column ${colIndex}`
              return (
                <button
                  key={idx}
                  type="button"
                  role="gridcell"
                  aria-rowindex={rowIndex}
                  aria-colindex={colIndex}
                  aria-label={cellLabel}
                  className={className}
                  onClick={onCellTap ? () => onCellTap(idx) : undefined}
                >
                  {view === 'letters' && letter ? (
                    <Cube
                      letter={letter}
                      size="small"
                      traced={traceIndices.includes(idx)}
                      frozen={frozenIdxs.includes(idx)}
                      drop={droppingIdxs.includes(idx)}
                      pulse={pulsingIdxs.includes(idx)}
                    />
                  ) : null}
                  {view === 'chess' && piece ? (
                    <Piece
                      kind={piece.kind}
                      player={piece.player}
                      // exactOptionalPropertyTypes: spread `empowered`
                      // only when defined so we never pass `undefined`
                      // to an optional `boolean`.
                      {...(piece.empowered !== undefined
                        ? { empowered: piece.empowered }
                        : {})}
                      selected={selectedIdx === idx}
                    />
                  ) : null}
                </button>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}
